import { gql, useQuery } from '@apollo/client';
import { PiezoToolCall } from '../../../client/piezo/signing/toolCalls';

export interface CWMetricsVars {
  domainIdentifier?: string;
  metrics: string;
  startTime: Date;
  endTime: Date;
  period: number;
  isSDPDSMetric?: boolean;
}

export interface CWMetricsData {
  cwMetrics?: {
    timeStamps: Date[];
    externalMetrics: {
      values: number[];
      label: string;
    };
  };
  sdpdsCwMetrics?: {
    timeStamps: Date[];
    externalMetrics: {
      values: number[];
      label: string;
    };
  };
}
const GQL_CW_METRICS = gql`
  query getExternalCwMetrics(
    $domainIdentifier: String!
    $metrics: String!
    $startTime: Date!
    $endTime: Date!
    $period: Int!
    $piezoArtifacts: JSON
  ) {
    cwMetrics(
      domainIdentifier: $domainIdentifier
      metrics: $metrics
      startTime: $startTime
      endTime: $endTime
      period: $period
      piezoArtifacts: $piezoArtifacts
    )
  }
`;

const GQL_SDPDS_CW_METRICS = gql`
  query getSDPDSCwMetrics(
    $metrics: String!
    $startTime: Date!
    $endTime: Date!
    $period: Int!
    $piezoArtifacts: JSON
  ) {
    sdpdsCwMetrics(
      metrics: $metrics
      startTime: $startTime
      endTime: $endTime
      period: $period
      piezoArtifacts: $piezoArtifacts
    )
  }
`;

export const useGetExternalCwQuery = (vars: CWMetricsVars) => {
  const {
    isSDPDSMetric,
    domainIdentifier,
    metrics,
    startTime,
    endTime,
    period
  } = vars;

  const metricsQueryParams = {
    metrics,
    startTime,
    endTime,
    period
  };

  const queryVars = isSDPDSMetric
    ? metricsQueryParams
    : { ...metricsQueryParams, domainIdentifier };

  // Build piezoToolCalls for the CW metrics query.
  // getCustomerCwMetrics sends: { cw_metrics: metrics, start_time: startTime, end_time: endTime,
  //   domain_identifier: domainIdentifier, period }
  // getSDPDSCwMetrics sends: { cw_metrics: metrics, start_time: startTime, end_time: endTime, period }
  // Note: startTime/endTime are Date objects that get serialized by Apollo as numbers (epoch ms).
  // Times are Date objects; sign them as epoch-ms strings — exactly what the tool receives.
  const toEpochMsString = (t: Date | number): string => String(t instanceof Date ? t.getTime() : t);
  const piezoToolCalls: PiezoToolCall[] = isSDPDSMetric
    ? [{
        key: 'sdpdsCwMetrics',
        toolId: 'aes/domain/cw/get-sdpds-cw-metrics',
        parameters: { cw_metrics: metrics, start_time: toEpochMsString(startTime), end_time: toEpochMsString(endTime), period },
      }]
    : [{
        key: 'cwMetrics',
        toolId: 'aes/domain/cw/get-external-cw-metrics',
        parameters: {
          cw_metrics: metrics,
          start_time: toEpochMsString(startTime),
          end_time: toEpochMsString(endTime),
          domain_identifier: domainIdentifier,
          period,
        },
      }];

  return useQuery<CWMetricsData, CWMetricsVars>(
    isSDPDSMetric ? GQL_SDPDS_CW_METRICS : GQL_CW_METRICS,
    {
      variables: queryVars,
      context: { piezoToolCalls },
    }
  );
};
